'use client';

import { useContext, useEffect, useRef, useState } from 'react';
import { EChartsOption } from 'echarts-for-react';
import { useWatchlistContext } from '@benzinga/user-context';
import { SessionContext } from '@benzinga/session-context';
import { Watchlist } from '@benzinga/watchlist-manager';

import {
  getWatchlistQuotes,
  getWatchlistHistoricalChartOptions,
  getCompanyDetails,
} from '../../data';
import { isCryptoOnlyWatchlist } from '../watchlistUtils';
import { buildWatchlistMixConfigs, WatchlistMixConfig } from '../utils';
import { processWatchlistStats, WatchlistStats } from '../WatchlistPerformance/processors';

export type InsightsData = {
  chartOptions: EChartsOption | null;
  pieConfigs: WatchlistMixConfig[];
  stats: WatchlistStats;
};

export const useWatchlistInsights = () => {
  const { selectedWatchlist } = useWatchlistContext();
  const session = useContext(SessionContext);
  const [data, setData] = useState<InsightsData | null>(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoadingChart, setIsLoadingChart] = useState(false);
  const [isLoadingMix, setIsLoadingMix] = useState(false);
  const dataRef = useRef<InsightsData | null>(null);
  const requestIdRef = useRef(0);
  const isCryptoOnly = isCryptoOnlyWatchlist(selectedWatchlist);

  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();
    const requestId = ++requestIdRef.current;

    const loadInsights = async (watchlist: Watchlist) => {
      const hasExistingData = dataRef.current !== null;
      setIsLoadingChart(false);

      if (hasExistingData) {
        setIsRefreshing(true);
      } else {
        setIsInitialLoading(true);
        setIsLoadingMix(!isCryptoOnly);
      }

      try {
        const watchlistFinancials = await getWatchlistQuotes(watchlist, session.getSession());
        if (abortController.signal.aborted || requestId !== requestIdRef.current) return;

        const stats = processWatchlistStats(watchlistFinancials);
        setIsLoadingChart(true);

        const chartPromise = getWatchlistHistoricalChartOptions(
          watchlistFinancials,
          session.getSession(),
        ).catch(error => {
          console.error('Error loading watchlist history chart', error);
          return null;
        });

        const detailsPromise = isCryptoOnly
          ? Promise.resolve([] as Awaited<ReturnType<typeof getCompanyDetails>>)
          : getCompanyDetails(
              watchlist.symbols.map(symbol => symbol.symbol),
              session.getSession(),
            );

        if (isMounted && requestId === requestIdRef.current) {
          const nextData = {
            chartOptions: null,
            pieConfigs: hasExistingData ? (dataRef.current?.pieConfigs ?? []) : [],
            stats,
          };
          dataRef.current = nextData;
          setData(nextData);
          setIsInitialLoading(false);
          setIsRefreshing(false);
        }

        const [details, chartOptions] = await Promise.all([detailsPromise, chartPromise]);
        if (!isMounted || requestId !== requestIdRef.current) return;

        const pieConfigs =
          !isCryptoOnly && details.length > 0
            ? buildWatchlistMixConfigs({
                regions: details.map(detail => detail.region || 'Other'),
                sectors: details.map(detail => detail.sector?.sector_name || 'Other'),
                watchlistName: watchlist.name,
              })
            : [];

        setData(previous => {
          if (!previous) return previous;
          const nextData = { ...previous, chartOptions, pieConfigs };
          dataRef.current = nextData;
          return nextData;
        });
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') return;
        console.error('Error loading watchlist insights', error);
        if (isMounted && requestId === requestIdRef.current && !hasExistingData) {
          dataRef.current = null;
          setData(null);
        }
      } finally {
        if (isMounted && requestId === requestIdRef.current) {
          setIsInitialLoading(false);
          setIsRefreshing(false);
          setIsLoadingChart(false);
          setIsLoadingMix(false);
        }
      }
    };

    if (selectedWatchlist?.symbols.length) {
      loadInsights(selectedWatchlist);
    } else {
      dataRef.current = null;
      setData(null);
      setIsLoadingChart(false);
      setIsLoadingMix(false);
      setIsInitialLoading(false);
      setIsRefreshing(false);
    }

    return () => {
      isMounted = false;
      abortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch on watchlist identity / size / crypto mode
  }, [session, isCryptoOnly, selectedWatchlist?.watchlistId, selectedWatchlist?.symbols?.length]);

  return {
    data,
    hasSymbols: Boolean(selectedWatchlist?.symbols.length),
    isCryptoOnly,
    isInitialLoading,
    isLoadingChart,
    isLoadingMix,
    isRefreshing,
  };
};
